import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BookmarkPlus, Loader2, Check } from "lucide-react";
import { API, VERTICALS, CONTENT_TYPES } from "./kbConstants";

/**
 * Reusable "Save to Knowledge Base" action. Drop it on any task output,
 * chat message, or report section. Opens a lightweight popover to pick a
 * scope, title, vertical and tags, then persists the content as a
 * KnowledgeEntry.
 */
export default function SaveToKnowledgeButton({
  content,
  defaultTitle = "",
  sourceType = "manual_upload",
  sourceReference = null,
  vertical: defaultVertical = "general",
  contentType = "insight",
  projectId = null,
  swarm = null,
  size = "sm",
  variant = "outline",
  label = "Save to Knowledge Base",
  className = "",
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [scopes, setScopes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const [scopeVal, setScopeVal] = useState(projectId ? `project:${projectId}` : "individual:");
  const [vertical, setVertical] = useState(defaultVertical);
  const [ctype, setCtype] = useState(contentType);
  const [tags, setTags] = useState("");
  const editable = !((content || "").trim());
  const [bodyText, setBodyText] = useState("");

  useEffect(() => { setTitle(defaultTitle); }, [defaultTitle]);

  const loadScopes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/kb/scopes`);
      setScopes(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (open && !scopes) loadScopes(); }, [open, scopes, loadScopes]);

  const scopeOptions = () => {
    if (!scopes) return [];
    const opts = [{ value: "individual:", label: "Individual (private)" }];
    (scopes.projects || []).forEach((p) =>
      opts.push({ value: `project:${p.scope_id}`, label: `Project · ${p.name}` }));
    (scopes.teams || []).forEach((t) =>
      opts.push({ value: `team:${t.scope_id}`, label: `Team · ${t.name}` }));
    if (scopes.organization?.can_write) {
      opts.push({ value: `organization:${scopes.organization.scope_id}`, label: "Organization (everyone)" });
    }
    return opts;
  };

  const save = async () => {
    const body = editable ? bodyText.trim() : (content || "").trim();
    if (!body) { toast.error("Nothing to save"); return; }
    const [scope, scope_id] = scopeVal.split(":");
    setSaving(true);
    try {
      await axios.post(`${API}/kb/entries`, {
        scope,
        scope_id: scope_id || null,
        title: (title || body.slice(0, 60)).trim(),
        body,
        content_type: ctype,
        source_type: sourceType,
        source_reference: sourceReference || (swarm ? { swarm } : {}),
        vertical,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setSaved(true);
      toast.success("Saved to Knowledge Base");
      onSaved && onSaved();
      setBodyText("");
      setTimeout(() => { setOpen(false); setSaved(false); }, 900);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size={size}
          variant={variant}
          className={`gap-1.5 ${className}`}
          data-testid="save-to-kb-trigger"
        >
          <BookmarkPlus className="w-3.5 h-3.5" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4 space-y-3" data-testid="save-to-kb-popover">
        <div className="text-sm font-semibold text-slate-800">Save to Knowledge Base</div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short, descriptive title"
            data-testid="save-to-kb-title"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Scope</label>
          <Select value={scopeVal} onValueChange={setScopeVal}>
            <SelectTrigger data-testid="save-to-kb-scope"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[70]">
              {scopeOptions().map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Vertical</label>
            <Select value={vertical} onValueChange={setVertical}>
              <SelectTrigger data-testid="save-to-kb-vertical"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[70]">
                {VERTICALS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Type</label>
            <Select value={ctype} onValueChange={setCtype}>
              <SelectTrigger data-testid="save-to-kb-type"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[70]">
                {CONTENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Tags (comma separated)</label>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. pricing, q3" data-testid="save-to-kb-tags" />
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 p-2 max-h-20 overflow-y-auto text-xs text-slate-500">
          {editable ? (
            <Textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={3}
              placeholder="Type the knowledge to save…"
              className="text-xs border-0 bg-transparent p-0 focus-visible:ring-0 resize-none"
              data-testid="save-to-kb-body"
            />
          ) : (
            (content || "").slice(0, 240) || "No content"
          )}
        </div>
        <Button
          onClick={save}
          disabled={saving || saved}
          className="w-full gap-1.5"
          data-testid="save-to-kb-confirm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <BookmarkPlus className="w-4 h-4" />}
          {saved ? "Saved" : "Save"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
